import type { JsonLdSchema } from 'interfaces/metaDataUtils/jsonLdSchema';
import type { PianoPageConfig } from 'services/Piano/entities/PianoPageConfig';
import { dateDifferenceInHours } from 'utils/dateUtils';

export function addPaywallSchema(schema: string, pageConfig: PianoPageConfig): string {
  try {
    const schemaObj = JSON.parse(schema) as JsonLdSchema;
    const paywallStory = isPaywallContent(pageConfig);

    if (paywallStory || pageConfig.franchiseWall) {
      schemaObj.isAccessibleForFree = false;
      schemaObj.hasPart = {
        '@type': 'WebPageElement',
        cssSelector: pageConfig.franchiseWall ? '.franchise-content' : '.paywall',
        isAccessibleForFree: 'False',
      };
    } else {
      schemaObj.isAccessibleForFree = true;
    }
    const schemaWithPaywall = JSON.stringify(schemaObj);
    return schemaWithPaywall;
  } catch {
    return schema;
  }
}

export function isPaywallContent(pageConfig: PianoPageConfig): boolean {
  const paywallExemptTags = ['Always Free', 'Personal Finance Evergreen', 'Health Evergreen'];

  return (
    (pageConfig.contentSection.startsWith('P') ||
      ['F-BreakingNews', 'F-Conference', 'F-Search', 'F-Service', 'F-Wire', 'F-Commentary', 'legacy'].includes(
        pageConfig.contentSection,
      )) &&
    paywallExemptTags.every((tag) => !pageConfig.tags.includes(tag))
  );
}

export function isSubscribePage() {
  return ['/subscribe/', '/paid-subscribe/', '/email-subscribe/'].some((path) =>
    window.location.pathname.includes(path),
  );
}

export function olderThan12Hours(publishDate: string): boolean {
  const duration = dateDifferenceInHours(new Date(publishDate), new Date());

  return duration > 12;
}

export function hasExcludedTag(tags: string[]): boolean {
  const excludedTopics: string[] = [
    'Always Free',
    'Battle for Talent',
    'Breakthrough!',
    'CFO Daily',
    'CHRO Daily',
    'Everyday AI',
    'Fortune Well',
    'Leadership Next',
    'RV Widget Test',
    'Tech Debate',
    'Tech Forward',
    'The Modern Board',
    'The Path to Zero',
    'The Trust Factor Newsletter',
  ];

  return tags.some((e) => excludedTopics.includes(e));
}

export function isAutoPaywallContent(pageConfig: PianoPageConfig): boolean {
  const publishedOver12hr = olderThan12Hours(pageConfig.publishDate);
  const excludedTag = hasExcludedTag(pageConfig.tags);

  let autoPaywall;
  switch (pageConfig.contentSection) {
    case 'F-BreakingNews':
    case 'F-Conference':
    case 'F-Search':
      autoPaywall = true;
      break;
    default:
      autoPaywall = false;
      break;
  }

  return autoPaywall && publishedOver12hr && !excludedTag;
}
